import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Filter, Grid, List, Search, X, ChevronDown, Award, Dog, Cat, Microscope, Puzzle, BookOpen, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { products } from '../data/products';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { Badge } from '../components/ui/Badge';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { AdvancedProductFilters } from '../components/products/AdvancedProductFilters';
import { RecentlyViewed } from '../components/products/RecentlyViewed';
import { Product } from '../utils/types';
import { useCart } from '../context/CartContext';
// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}
export function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('featured');
  const [isLoading, setIsLoading] = useState(true);
  // Quick View State
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const {
    addToCart
  } = useCart();
  // Filter States
  const [filters, setFilters] = useState({
    category: [] as string[],
    petType: [] as string[],
    priceRange: [0, 200] as [number, number],
    dietaryNeeds: [] as string[],
    brand: [] as string[],
    rating: null as number | null
  });
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const categoryParam = searchParams.get('category');
  // Initialize filters from URL
  useEffect(() => {
    if (categoryParam) {
      setFilters(prev => ({
        ...prev,
        category: [categoryParam]
      }));
    }
  }, [categoryParam]);
  // Simulate loading
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [filters, debouncedSearchQuery, sortBy]);
  // Update URL on search
  useEffect(() => {
    if (debouncedSearchQuery) {
      setSearchParams(prev => {
        prev.set('search', debouncedSearchQuery);
        return prev;
      });
    } else {
      setSearchParams(prev => {
        prev.delete('search');
        return prev;
      });
    }
  }, [debouncedSearchQuery, setSearchParams]);
  const filteredProducts = useMemo(() => {
    let result = [...products];
    // Category Filter
    if (filters.category.length > 0) {
      result = result.filter(p => filters.category.includes(p.category));
    }
    // Search Filter
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(query) || p.description.toLowerCase().includes(query));
    }
    // Pet Type Filter
    if (filters.petType.length > 0) {
      result = result.filter(p => filters.petType.includes(p.petType) || p.petType === 'both');
    }
    // Dietary Needs (Mock implementation - assumes tags or description contains info)
    if (filters.dietaryNeeds.length > 0) {
      result = result.filter(p => {
        const text = (p.description + ' ' + (p.tags?.join(' ') || '')).toLowerCase();
        return filters.dietaryNeeds.some(need => text.includes(need.toLowerCase()));
      });
    }
    // Price Filter
    result = result.filter(p => p.price >= filters.priceRange[0] && p.price <= filters.priceRange[1]);
    // Sorting
    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        result.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
      case 'rating':
        result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'popular':
        result.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
        break;
      default:
        break;
    }
    return result;
  }, [filters, debouncedSearchQuery, sortBy]);
  const openQuickView = (product: Product) => {
    setQuickViewProduct(product);
    setIsQuickViewOpen(true);
  };
  const activeFiltersCount = filters.category.length + filters.petType.length + filters.dietaryNeeds.length + (filters.priceRange[0] > 0 || filters.priceRange[1] < 200 ? 1 : 0);
  return <div className="bg-gray-50 min-h-screen pb-24 font-sans" data-id="element-6649">
      {/* Breadcrumb */}
      <div className="container-custom pt-24 pb-4" data-id="element-6650">
        <Breadcrumb items={[{
        label: 'Inicio',
        href: '/'
      }, {
        label: 'Productos',
        href: '/productos',
        active: true
      }]} data-id="element-6651" />
      </div>

      {/* Hero Section */}
      <div className="bg-charcoal-900 text-white py-16 lg:py-20 mb-12 relative overflow-hidden rounded-3xl mx-4 lg:mx-8 shadow-2xl" data-id="element-6652">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] mix-blend-overlay" data-id="element-6653"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-900/40 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-6654"></div>

        <div className="container-custom relative z-10" data-id="element-6655">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-6656">
            <div className="text-left" data-id="element-6657">
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 text-white text-xs font-bold mb-6 tracking-wide uppercase shadow-lg" data-id="element-6658">
                <Award className="h-3.5 w-3.5 text-secondary-400" data-id="element-6659" />
                <span data-id="element-6660">Catálogo Premium Verificado</span>
              </motion.div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight font-serif tracking-tight text-white" data-id="element-6661">
                Nutrición que <br data-id="element-6662" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400" data-id="element-6663">
                  Transforma Vidas
                </span>
              </h1>
            </div>

            {/* Quick Categories */}
            <div className="grid grid-cols-2 gap-4" data-id="element-6664">
              {[{
              id: 'dog-food',
              label: 'Perros',
              icon: Dog,
              color: 'bg-orange-500'
            }, {
              id: 'cat-food',
              label: 'Gatos',
              icon: Cat,
              color: 'bg-blue-500'
            }, {
              id: 'care',
              label: 'Cuidado',
              icon: Microscope,
              color: 'bg-emerald-500'
            }, {
              id: 'toys',
              label: 'Juguetes',
              icon: Puzzle,
              color: 'bg-purple-500'
            }].map(cat => <button key={cat.id} onClick={() => setFilters(prev => ({
              ...prev,
              category: [cat.id]
            }))} className="p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:scale-105 transition-all group text-left" data-id="element-6665">
                  <div className={`w-10 h-10 rounded-full ${cat.color} flex items-center justify-center mb-4 shadow-lg`} data-id="element-6666">
                    <cat.icon className="w-5 h-5 text-white" data-id="element-6667" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1" data-id="element-6668">
                    {cat.label}
                  </h3>
                </button>)}
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6669">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start" data-id="element-6670">
          {/* Sidebar Filters */}
          <aside className="hidden lg:block w-72 flex-shrink-0 sticky top-28 space-y-8" data-id="element-6671">
            <div className="bg-white p-6 rounded-2xl shadow-soft-md border border-gray-100" data-id="element-6672">
              <AdvancedProductFilters onFilterChange={setFilters} onSearch={setSearchQuery} totalProducts={filteredProducts.length} initialFilters={filters} data-id="element-6673" />
            </div>

            {/* Widgets */}
            <div className="bg-primary-50 p-6 rounded-2xl border border-primary-100" data-id="element-6674">
              <div className="flex items-center gap-2 mb-4" data-id="element-6675">
                <BookOpen className="w-5 h-5 text-primary-600" data-id="element-6676" />
                <h3 className="font-bold text-primary-900" data-id="element-6677">Guía de Compra</h3>
              </div>
              <p className="text-sm text-primary-800 mb-4 leading-relaxed" data-id="element-6678">
                ¿No sabes qué alimento es mejor para tu mascota?
              </p>
              <Link to="/blog/guia-nutricion" className="text-xs font-bold text-primary-700 hover:text-primary-900 flex items-center gap-1" data-id="element-6679">
                Leer guía completa <ArrowRight className="w-3 h-3" data-id="element-6680" />
              </Link>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 w-full min-w-0" data-id="element-6681">
            {/* Toolbar */}
            <div className="bg-white p-4 rounded-xl shadow-soft-sm border border-gray-100 mb-8 flex flex-col sm:flex-row justify-between items-center gap-4 sticky top-24 z-30 transition-all duration-300 backdrop-blur-md bg-white/95" data-id="element-6682">
              <div className="flex items-center gap-3 pl-2" data-id="element-6683">
                <span className="text-charcoal-500 text-sm font-medium" data-id="element-6684">
                  Mostrando{' '}
                  <span className="font-bold text-charcoal-900" data-id="element-6685">
                    {filteredProducts.length}
                  </span>{' '}
                  resultados
                </span>
                {activeFiltersCount > 0 && <Badge variant="secondary" size="sm" className="hidden sm:flex" data-id="element-6686">
                    Filtros activos: {activeFiltersCount}
                  </Badge>}
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-6687">
                <Button variant="outline" className="lg:hidden flex-1 sm:flex-none text-sm h-10 rounded-lg border-gray-200" onClick={() => setIsMobileFiltersOpen(true)} data-id="element-6688">
                  <Filter className="h-4 w-4 mr-2" data-id="element-6689" /> Filtros
                </Button>

                <div className="flex items-center gap-1 border-l border-gray-200 pl-3" data-id="element-6690">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-gray-100 text-charcoal-900' : 'text-charcoal-400 hover:text-charcoal-600'}`} data-id="element-6691">
                    <Grid className="h-4 w-4" data-id="element-6692" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-gray-100 text-charcoal-900' : 'text-charcoal-400 hover:text-charcoal-600'}`} data-id="element-6693">
                    <List className="h-4 w-4" data-id="element-6694" />
                  </button>
                </div>

                <div className="relative w-full sm:w-auto sm:min-w-[180px]" data-id="element-6695">
                  <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="w-full appearance-none bg-gray-50 border border-gray-200 text-charcoal-800 py-2.5 px-3 pr-8 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-sm font-medium cursor-pointer transition-all hover:bg-gray-100" data-id="element-6696">
                    <option value="featured" data-id="element-6697">Destacados</option>
                    <option value="popular" data-id="element-6698">Más Populares</option>
                    <option value="price-asc" data-id="element-6699">Precio: Menor a Mayor</option>
                    <option value="price-desc" data-id="element-6700">Precio: Mayor a Menor</option>
                    <option value="newest" data-id="element-6701">Más Nuevos</option>
                    <option value="rating" data-id="element-6702">Mejor Valorados</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-400 pointer-events-none" data-id="element-6703" />
                </div>
              </div>
            </div>

            {/* Product Grid */}
            {isLoading ? <div className={`grid gap-8 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`} data-id="element-6704">
                {[...Array(6)].map((_, i) => <div key={i} className="bg-white rounded-[1.5rem] p-4 border border-gray-100 h-[420px] flex flex-col shadow-sm" data-id="element-6705">
                    <Skeleton className="w-full aspect-[4/5] rounded-xl mb-4" data-id="element-6706" />
                    <Skeleton className="h-4 w-20 mb-2 rounded-full" data-id="element-6707" />
                    <Skeleton className="h-6 w-3/4 mb-3 rounded-lg" data-id="element-6708" />
                    <div className="mt-auto flex justify-between items-center" data-id="element-6709">
                      <Skeleton className="h-8 w-24 rounded-lg" data-id="element-6710" />
                      <Skeleton className="h-10 w-10 rounded-xl" data-id="element-6711" />
                    </div>
                  </div>)}
              </div> : filteredProducts.length > 0 ? <div className={`grid gap-8 lg:gap-10 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`} data-id="element-6712">
                <AnimatePresence mode="popLayout" data-id="element-6713">
                  {filteredProducts.map(product => <ProductCard key={product.id} product={product} onQuickView={openQuickView} data-id="element-6714" />)}
                </AnimatePresence>
              </div> : <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200 shadow-soft-sm" data-id="element-6715">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-50 mb-6 shadow-inner" data-id="element-6716">
                  <Search className="h-8 w-8 text-gray-400" data-id="element-6717" />
                </div>
                <h3 className="text-2xl font-bold text-charcoal-800 mb-3 font-serif" data-id="element-6718">
                  No se encontraron productos
                </h3>
                <p className="text-charcoal-500 max-w-md mx-auto mb-8 text-base leading-relaxed" data-id="element-6719">
                  Intenta ajustar tus filtros o búsqueda.
                </p>
                <Button onClick={() => setFilters({
              category: [],
              petType: [],
              priceRange: [0, 200],
              dietaryNeeds: [],
              brand: [],
              rating: null
            })} className="rounded-xl px-8 py-3 shadow-lg" data-id="element-6720">
                  Limpiar filtros
                </Button>
              </motion.div>}
          </main>
        </div>
      </div>

      {/* Recently Viewed */}
      <RecentlyViewed data-id="element-6721" />

      {/* Mobile Filters */}
      <AnimatePresence data-id="element-6722">
        {isMobileFiltersOpen && <div className="fixed inset-0 z-[100] flex lg:hidden" data-id="element-6723">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileFiltersOpen(false)} data-id="element-6724" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="relative w-full max-w-xs bg-white h-full shadow-2xl ml-auto flex flex-col" data-id="element-6725">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gray-50" data-id="element-6726">
                <h2 className="text-lg font-bold text-charcoal-800 font-serif" data-id="element-6727">
                  Filtros
                </h2>
                <button onClick={() => setIsMobileFiltersOpen(false)} className="p-2 text-charcoal-400 hover:text-charcoal-900 rounded-full hover:bg-white transition-colors" data-id="element-6728">
                  <X className="h-5 w-5" data-id="element-6729" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5" data-id="element-6730">
                <AdvancedProductFilters onFilterChange={setFilters} onSearch={setSearchQuery} totalProducts={filteredProducts.length} initialFilters={filters} data-id="element-6731" />
              </div>
              <div className="p-5 border-t border-gray-100 bg-gray-50" data-id="element-6732">
                <Button fullWidth onClick={() => setIsMobileFiltersOpen(false)} data-id="element-6733">
                  Ver {filteredProducts.length} productos
                </Button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}